<script setup lang="tsx">
import type { Audit } from '@/models/api-generated'
import { BasicAudit, InternalAudit, ForeignInternalAudit } from '@/models/enums-generated'
import { useI18n } from 'vue-i18n'
import Multiselect from 'vue-multiselect'
import { formatDate } from '@/services/utils'

const model = defineModel<Audit>()
const props = defineProps<{
	options: Audit[]
	metadataId?: number
}>()

const { t } = useI18n()

const icon = (ctx: { a: Audit }) => {
	if (ctx.a.type === BasicAudit)
		return (
			<span class="badge bg-primary" title={t('audit.basicAudit')}>
				{t('audit.basicAbbr')}
			</span>
		)
	else if (ctx.a.type === InternalAudit) {
		if (ctx.a.isPublic)
			return (
				<span class="badge bg-primary" title={t('misc.public') + ' ' + t('audit.internalAudit')}>
					{t('audit.publicInternalAbbr')}
				</span>
			)
		else
			return (
				<span class="badge bg-primary" title={t('audit.internalAudit')}>
					{t('audit.privateInternaltAbbr')}
				</span>
			)
	} else if (ctx.a.type === ForeignInternalAudit) {
		return (
			<span class="badge bg-primary" title={t('audit.foreignInternalAudit')}>
				{t('audit.foreignInternalAbbr')}
			</span>
		)
	}
	return <i class="bi bi-cross"></i>
}

const item = (ctx: { a: Audit }) => {
	const numComments = props.metadataId ? ctx.a.numCommentsByMethod[props.metadataId] ?? 0 : ctx.a.numCommentsTotal
	const instance = ctx.a.instance?.name ?? t('misc.all')

	return (
		<div class="d-flex flex-wrap align-items-center gap-1">
			<icon a={ctx.a}></icon>
			<span class="badge bg-light text-dark audit-badge-ellipsis" v-one-tooltip={ctx.a.name}>
				{ctx.a.name}
			</span>
			<span class="text-truncate text-muted small d-flex align-items-center" v-one-tooltip={instance}>
				<i class="bi bi-building me-1"></i>
				{instance}
			</span>
			<span class="text-muted small d-flex align-items-center">
				<i class="bi bi-calendar me-1"></i>
				{formatDate(ctx.a.createdAt)}
			</span>
			<span class="text-muted small d-flex align-items-center">
				<i class="bi bi-chat-left me-1"></i> {numComments}
			</span>
		</div>
	)
}
</script>

<template>
	<Multiselect
		:searchable="false"
		:allow-empty="false"
		:preselect-first="true"
		:show-labels="false"
		:use-teleport="true"
		v-model="model"
		:options="props.options"
	>
		<template #singleLabel="{ option }">
			<item :a="option"></item>
		</template>
		<template #option="{ option }">
			<item :a="option"></item>
		</template>
	</Multiselect>
</template>

<style lang="sass">
.audit-badge-ellipsis
  overflow: clip
  text-overflow: ellipsis
  white-space: nowrap
  max-width: 8em
</style>
